import React, { useEffect, useState } from "react";
import api from "../services/api";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Alert, Box,
} from "@mui/material";

/* =====================================================================
 * Send the price list to a few chosen people.
 *
 * Not a mailing list - "send it to these two or three", which is how a
 * yard actually shares a price. Addresses are typed in, because the
 * supplier records have none stored yet and waiting for that would make
 * the feature useless today.
 *
 * The list goes as a PDF attachment, built by the server, so what arrives
 * in a supplier's inbox is the same document the Download button gives.
 * ===================================================================== */
export default function EmailPriceListDialog({ open, onClose, company, priceDate, count }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTo("");
    setSent(null);
    setError(null);
    const name = (company && company.Name) || "Price List";
    setSubject(name + " - Price List (" + (priceDate || "current") + ")");
    setMessage("Please find our current price list attached.");
  }, [open, company, priceDate]);

  if (!open) return null;

  const addresses = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const bad = addresses.filter((a) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a));
  const canSend = addresses.length > 0 && bad.length === 0 && !sending;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const r = await api.post("/priceMappings/emailPriceList", {
        to: addresses, subject, message,
      });
      setSent(r.data);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Email the price list</DialogTitle>
      <DialogContent dividers>
        {sent ? (
          <Alert severity="success">
            <strong>{sent.message}</strong>
            <Box sx={{ mt: 1 }}>{(sent.sentTo || []).join(", ")}</Box>
            {(sent.refused || []).length > 0 && (
              <Box sx={{ mt: 1, color: "error.main" }}>
                Refused: {(sent.refused || []).join(", ")}
              </Box>
            )}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              The list goes out as a PDF attachment — {count} material
              {count === 1 ? "" : "s"}, priced {priceDate || "today"}. Only the
              addresses below receive it.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              label="Send to"
              placeholder="name@company.com, second@company.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              error={bad.length > 0}
              helperText={
                bad.length > 0
                  ? "Not an email address: " + bad.join(", ")
                  : addresses.length
                  ? addresses.length + " recipient" + (addresses.length === 1 ? "" : "s")
                  : "Separate several with commas"
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              minRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{sent ? "Close" : "Cancel"}</Button>
        {!sent && (
          <Button variant="contained" disabled={!canSend} onClick={send}>
            {sending
              ? "Sending…"
              : "Send" + (addresses.length ? " to " + addresses.length : "")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
